import { ChevronRight, SearchX } from "lucide-react";
import { CompanyLogo } from "@/components/CompanyLogo";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type { BizminerRelatedInitiative } from "@/types/serializers";

interface RelatedInitiativesTableProps {
  relatedInitiatives: BizminerRelatedInitiative[];
  onSelect: (index: number) => void;
}

const COLUMNS = ["Company", "Impact Category", "Related Initiative", "Pillar", "Beneficiary", "Evidence Type"] as const;
const thCls = "h-9 px-6 font-semibold text-xs uppercase tracking-wider";

export function RelatedInitiativesTable({
  relatedInitiatives,
  onSelect,
}: RelatedInitiativesTableProps) {
  if (relatedInitiatives.length === 0) {
    return (
      <div className="text-center py-16">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-muted mb-4">
          <SearchX className="h-8 w-8 text-muted-foreground" />
        </div>
        <h3 className="text-lg font-semibold mb-1">No related initiatives found</h3>
        <p className="text-sm text-muted-foreground">Try adjusting your filters to find what you're looking for.</p>
      </div>
    );
  }

  return (
    <Card className="overflow-hidden p-0">
      <Table>
        <TableHeader className="bg-muted/50">
          <TableRow className="hover:bg-muted/50">
            {COLUMNS.map((col) => (
              <TableHead key={col} className={thCls}>{col}</TableHead>
            ))}
            <TableHead className="h-9 w-10" />
          </TableRow>
        </TableHeader>
        <TableBody className="[&_tr:last-child]:border-b">
          {relatedInitiatives.map((ri, index) => (
            <TableRow
              key={`${ri.account.name}-${ri.pillar_title}-${ri.name}-${index}`}
              className="cursor-pointer"
              onClick={() => onSelect(index)}
            >
              <TableCell className="px-6 py-3.5">
                <div className="flex items-center gap-3">
                  <CompanyLogo
                    logoUrl={ri.account.logo_url}
                    name={ri.account.name}
                    className="size-8 rounded-lg flex-shrink-0"
                  />
                  <span className="font-medium">{ri.account.name}</span>
                </div>
              </TableCell>
              <TableCell className="px-6 py-3.5">
                <div className="flex flex-wrap gap-1">
                  {ri.business_impact_type.map((type) => (
                    <Badge key={type} variant="secondary" className="text-xs">
                      {type}
                    </Badge>
                  ))}
                </div>
              </TableCell>
              <TableCell className="px-6 py-3.5 font-medium max-w-[280px] truncate">
                {ri.name}
              </TableCell>
              <TableCell className="px-6 py-3.5 text-muted-foreground">
                {ri.pillar_title}
              </TableCell>
              <TableCell className="px-6 py-3.5">
                <Badge variant="outline" className="text-xs">
                  {ri.beneficiary}
                </Badge>
              </TableCell>
              <TableCell className="px-6 py-3.5">
                {ri.evidence_type ? (
                  <Badge variant="secondary" className="text-xs">
                    {ri.evidence_type}
                  </Badge>
                ) : (
                  <span className="text-muted-foreground">—</span>
                )}
              </TableCell>
              <TableCell className="px-6 py-3.5">
                <button
                  type="button"
                  className="p-1 rounded-md hover:bg-muted transition-colors"
                  aria-label={`View details for ${ri.name}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    onSelect(index);
                  }}
                >
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                </button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
}
